import { useState, useRef, useEffect } from 'react';
import PageHero from '../components/ui/PageHero';
import {
  LuMail, LuPhone, LuMapPin, LuClock, LuSend, LuNewspaper,
  LuFileText, LuDownload, LuChevronDown, LuCircleCheck,
  LuMessageCircle, LuStar, LuUsers, LuZap,
} from 'react-icons/lu';

const IMG = {
  hero: 'https://player.vimeo.com/video/1119296918?muted=1&controls=0&loop=1&app_id=122963',
  team: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=700&q=80&auto=format',
  small: 'https://alstefgroup.com/wp-content/uploads/2024/09/XSORT-2024-HD-scaled.jpg',
};

const FAQS = [
  { q: 'How fast does an airport conveyor belt move?', a: 'Belts operate between 0.5 and 10 m/s depending on the section. Check-in belts move slowly for safety; underground transfer tunnels run much faster to cover long distances quickly.' },
  { q: 'What happens if my bag gets stuck?', a: 'Sensors detect jams instantly and alert the control room. The affected section pauses automatically, a technician is dispatched, and your bag is manually retrieved and re-entered into the system.' },
  { q: 'Can conveyor belts work 24/7?', a: 'Yes. Modern systems are designed for continuous operation. Maintenance is performed on isolated sections while the rest keeps running — no full system shutdowns are needed during normal operations.' },
  { q: 'Who controls the baggage handling system?', a: 'A dedicated control room team monitors everything via the Baggage Management System (BMS). Most decisions are automated by software, with human operators handling exceptions and emergencies.' },
  { q: 'How are lost bags tracked?', a: 'Every bag is scanned at multiple checkpoints. The BMS logs each scan with a timestamp and location. If a bag goes missing, operators trace its last known scan point and search from there.' },
  { q: 'What is the difference between barcode and RFID?', a: 'Barcodes need line-of-sight scanning at fixed points. RFID broadcasts continuously and can be read through packaging from up to 5 metres away — achieving ~99.9% vs ~85% read rate for barcodes.' },
  { q: 'How often are systems maintained?', a: 'Daily visual inspections, weekly motor and sensor tests, monthly full system audits, and annual complete overhauls. IoT sensor data enables predictive maintenance, flagging issues weeks in advance.' },
];

const CONTACT_INFO = [
  { icon: <LuMail size={18} />,   color: '#7c3aed', bg: '#ede9fe', title: 'Email',    detail: 'info@kiinnovation.in',             sub: 'We respond within 24 hours' },
  { icon: <LuPhone size={18} />,  color: '#0891b2', bg: '#cffafe', title: 'Phone',    detail: '+91 92281 66525',                  sub: 'Mon–Fri, 09:00–18:00 UTC' },
  { icon: <LuMapPin size={18} />, color: '#db2777', bg: '#fce7f3', title: 'Location', detail: 'Airport Technology Hub, Terminal 3', sub: 'By appointment only' },
  // { icon: <LuClock size={18} />,  color: '#d97706', bg: '#fff7ed', title: 'Hours',    detail: 'Monday – Friday',                    sub: '09:00 to 18:00 UTC' },
];

const STATS = [
  { icon: <LuUsers size={18} />, value: '500+',    label: 'Happy Customers' },
  { icon: <LuZap size={18} />,   value: '24 h',   label: 'Response Time' },
  { icon: <LuStar size={18} />,  value: '4.9 / 5', label: 'Service Quality' },
];

function Contact() {
  const [form, setForm]   = useState({ name: '', email: '', subject: '', message: '' });
  const [nl, setNl]       = useState('');
  const [toast, setToast] = useState({ msg: '', show: false });
  const [openFaq, setFaq] = useState(null);
  const timer             = useRef(null);
  const videoRef          = useRef(null);

  const showToast = (msg) => {
    clearTimeout(timer.current);
    setToast({ msg, show: true });
    timer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    showToast("Message sent! We'll get back to you within 24 hours.");
    setForm({ name: '', email: '', subject: '', message: '' });
  };


  return (
    <div className="page-enter">
      <PageHero
        tag="Get In Touch"
        title={<>Contact <span className="text-gradient">KI INNOVATION</span></>}
        subtitle="Have questions about airport baggage systems? Want to request a consultation or download our free technical brochure? We're here to help."
        breadcrumb={[{ label: 'Contact' }]}
      >
        <div style={{ position: 'relative', width: '100%', maxWidth: '500px', height: '300px' }}>
          {/* <div className="page-hero-img-card" style={{ marginBottom: 0, borderRadius: '12px', overflow: 'hidden', width: '100%', height: '100%' }}>
            <iframe
              ref={videoRef}
              src={IMG.hero}
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '12px' }}
              allow="autoplay; fullscreen"
              allowFullScreen
              title="Airport conveyor system video"
            />
          </div> */}
          <div className="video-frame-wrap reveal">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            poster="https://i.pinimg.com/1200x/8d/bd/fe/8dbdfe8a765da3ea5c860a62d34da7fd.jpg"
            className="local-video"
            onEnded={() => {
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
                videoRef.current.play();
              }
            }}
          >
            <source src="/howit.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
          <div style={{ position: 'absolute', bottom: '-20px', right: '-20px', width: '120px', height: '120px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '4px solid var(--bg)' }}>
            <img src={IMG.small} alt="Airport detail" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>
      </PageHero>

      {/* ══════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════ */}
      <div className="bg-dark-2" style={{ borderBottom: '1px solid var(--border-light)' }}>
        <div className="container">
          <div className="ct-stats-bar">
            {STATS.map((s, i) => (
              <div key={i} className="ct-stat-item">
                <div className="ct-stat-icon">{s.icon}</div>
                <div>
                  <div className="ct-stat-value">{s.value}</div>
                  <div className="ct-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          CONTACT GRID
      ══════════════════════════════════════ */}
      <section className="page-section bg-dark-2" id="contact">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">Contact Us</div>
            <h2 className="section-title">
              We'd Love to <span className="text-gradient">Hear From You</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Whether you're an aviation professional, student, or simply curious — reach out
              and our team will get back to you within 24 hours.
            </p>
          </div>

          <div className="contact-grid">

            {/* ── LEFT: Form ── */}
            <div className="contact-form-card reveal-left">
              {/* Card header */}
              <div className="ct-form-header">
                <div className="ct-form-header-icon">
                  <LuSend size={20} />
                </div>
                <div>
                  <h2 className="ct-form-title">Send Us a Message</h2>
                  <p className="ct-form-sub">Fill in the form and we'll respond within 24 hours.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="c-name">Full Name</label>
                    <input id="c-name" type="text" placeholder="Your full name"
                      value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="c-email">Email Address</label>
                    <input id="c-email" type="email" placeholder="your@email.com"
                      value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="c-subject">Subject</label>
                  <select id="c-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} required>
                    <option value="">Select a topic…</option>
                    <option value="general">General Enquiry</option>
                    <option value="technical">Technical Information</option>
                    <option value="consultation">Request a Consultation</option>
                    <option value="partnership">Partnership / Media</option>
                    <option value="research">Research / Academia</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="c-message">Message</label>
                  <textarea id="c-message" placeholder="Tell us what you'd like to know or discuss…"
                    value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required />
                </div>

                <div className="ct-form-footer">
                  <button type="submit" className="btn btn-primary">
                    <LuSend size={16} /> Send Message
                  </button>
                  <span className="ct-form-note">
                    <LuCircleCheck size={14} style={{ color: 'var(--accent-3)' }} />
                    We never share your data
                  </span>
                </div>
              </form>
            </div>

            {/* ── RIGHT: Info ── */}
            <div className="reveal-right">
              <div className="contact-info-stack">

                {/* Team image */}
                <div className="contact-team-img">
                  <img src={IMG.team} alt="KI INNOVATION team" loading="lazy" />
                  <div className="contact-team-overlay">
                    <LuMessageCircle size={18} style={{ color: '#fff', flexShrink: 0 }} />
                    <span>Our team is ready to help</span>
                  </div>
                </div>

                {/* Contact info cards */}
                <div className="ct-info-grid">
                  {CONTACT_INFO.map((item, i) => (
                    <div key={i} className="ct-info-card">
                      <div className="ct-info-card-icon" style={{ background: item.bg, color: item.color, border: `1px solid ${item.color}22` }}>
                        {item.icon}
                      </div>
                      <div className="ct-info-card-text">
                        <div className="ct-info-card-title">{item.title}</div>
                        <div className="ct-info-card-detail">{item.detail}</div>
                        <div className="ct-info-card-sub">{item.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Newsletter */}
                <div className="newsletter-card" id="newsletter">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LuNewspaper size={16} style={{ color: 'var(--primary)' }} /> Newsletter
                  </h4>
                  <p>Stay updated with the latest innovations in airport technology.</p>
                  <form onSubmit={e => { e.preventDefault(); showToast("You're subscribed!"); setNl(''); }} noValidate>
                    <div className="nl-row">
                      <input type="email" placeholder="Enter your email" value={nl}
                        onChange={e => setNl(e.target.value)} required aria-label="Newsletter email" />
                      <button type="submit" className="btn btn-sm btn-primary">Subscribe</button>
                    </div>
                  </form>
                </div>

                {/* Brochure */}
                {/* <button className="brochure-btn" id="brochure"
                  onClick={() => showToast('Brochure download starting…')}
                  aria-label="Download free PDF brochure">
                  <LuFileText size={20} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>Download Technical Brochure</div>
                    <div style={{ fontSize: '.79rem', color: 'var(--text-muted)', fontWeight: 400 }}>Comprehensive Guide (PDF) — Free</div>
                  </div>
                  <LuDownload size={17} />
                </button> */}

              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══════════════════════════════════════
          FAQ
      ══════════════════════════════════════ */}
      <section className="page-section" id="faq">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">FAQ</div>
            <h2 className="section-title">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Everything you need to know about airport baggage conveyor systems.
            </p>
          </div>

          <div className="faq-wrap">
            {FAQS.map((f, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="faq-q" onClick={() => setFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="faq-q-text">{f.q}</span>
                  <span className="faq-arrow" aria-hidden="true"><LuChevronDown size={14} /></span>
                </button>
                <div className="faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Toast */}
      <div className={`toast${toast.show ? '' : ' hidden'}`} role="status" aria-live="polite">
        <LuCircleCheck size={18} style={{ color: 'var(--accent-3)', flexShrink: 0 }} />
        {toast.msg}
      </div>
    </div>
  );
}

export default Contact;
